import { useI18n } from '../contexts/I18nContext';

export interface RTLAwareStyles {
  marginStart: string;
  marginEnd: string;
  paddingStart: string;
  paddingEnd: string;
  borderStart: string;
  borderEnd: string;
  insetStart: string;
  insetEnd: string;
  textAlign: 'left' | 'right' | 'start' | 'end';
  flexDirection: 'row' | 'row-reverse';
}

export interface RTLUtilities {
  isRTL: boolean;
  dir: 'ltr' | 'rtl';
  getMarginStart: (value: string) => { [key: string]: string };
  getMarginEnd: (value: string) => { [key: string]: string };
  getPaddingStart: (value: string) => { [key: string]: string };
  getPaddingEnd: (value: string) => { [key: string]: string };
  getBorderStart: (value: string) => { [key: string]: string };
  getBorderEnd: (value: string) => { [key: string]: string };
  getInsetStart: (value: string) => { [key: string]: string };
  getInsetEnd: (value: string) => { [key: string]: string };
  getFlexDirection: (reverse?: boolean) => 'row' | 'row-reverse';
  getTextAlign: (align?: 'start' | 'end' | 'center') => 'left' | 'right' | 'center' | 'start' | 'end';
  getTransform: (shouldFlip: boolean) => string;
  getDirectionalClass: (baseClass: string, direction: 'start' | 'end') => string;
}

export const useRTL = (): RTLUtilities => {
  const { isRTL } = useI18n();
  const dir = isRTL ? 'rtl' : 'ltr';

  const getMarginStart = (value: string) => ({
    marginInlineStart: value
  });

  const getMarginEnd = (value: string) => ({
    marginInlineEnd: value
  });

  const getPaddingStart = (value: string) => ({
    paddingInlineStart: value
  });

  const getPaddingEnd = (value: string) => ({
    paddingInlineEnd: value
  });

  const getBorderStart = (value: string) => ({
    borderInlineStart: value
  });

  const getBorderEnd = (value: string) => ({
    borderInlineEnd: value
  });

  const getInsetStart = (value: string) => ({
    insetInlineStart: value
  });

  const getInsetEnd = (value: string) => ({
    insetInlineEnd: value
  });

  const getFlexDirection = (reverse = false): 'row' | 'row-reverse' => {
    if (reverse) {
      return isRTL ? 'row' : 'row-reverse';
    }
    return isRTL ? 'row-reverse' : 'row';
  };

  const getTextAlign = (align: 'start' | 'end' | 'center' = 'start'): 'left' | 'right' | 'center' | 'start' | 'end' => {
    if (align === 'center') return 'center';
    if (align === 'start') return 'start';
    if (align === 'end') return 'end';
    return align;
  };

  const getTransform = (shouldFlip: boolean): string => {
    if (!shouldFlip) return '';
    return isRTL ? 'scaleX(-1)' : '';
  };

  const getDirectionalClass = (baseClass: string, direction: 'start' | 'end'): string => {
    const suffix = direction === 'start' ? 's' : 'e';
    return `${baseClass}-${suffix}`;
  };

  return {
    isRTL,
    dir,
    getMarginStart,
    getMarginEnd,
    getPaddingStart,
    getPaddingEnd,
    getBorderStart,
    getBorderEnd,
    getInsetStart,
    getInsetEnd,
    getFlexDirection,
    getTextAlign,
    getTransform,
    getDirectionalClass
  };
};

// Utility function to generate RTL-aware CSS classes
export const rtlClasses = {
  // Margin utilities
  ms: (size: string) => `ms-${size}`,
  me: (size: string) => `me-${size}`,
  
  // Padding utilities
  ps: (size: string) => `ps-${size}`,
  pe: (size: string) => `pe-${size}`,
  
  // Border utilities
  borderS: 'border-s',
  borderE: 'border-e',
  
  // Position utilities
  start: (size: string) => `start-${size}`,
  end: (size: string) => `end-${size}`,
  
  // Text alignment
  textStart: 'text-start',
  textEnd: 'text-end',
  
  // Flex utilities
  flexRowReverse: 'flex-row-reverse-rtl'
};

// CSS-in-JS helper for RTL-aware styles
export const createRTLStyles = (isRTL: boolean) => ({
  marginStart: (value: string | number) => ({
    marginInlineStart: value
  }),
  marginEnd: (value: string | number) => ({
    marginInlineEnd: value
  }),
  paddingStart: (value: string | number) => ({
    paddingInlineStart: value
  }),
  paddingEnd: (value: string | number) => ({
    paddingInlineEnd: value
  }),
  borderStart: (value: string) => ({
    borderInlineStart: value
  }),
  borderEnd: (value: string) => ({
    borderInlineEnd: value
  }),
  insetStart: (value: string | number) => ({
    insetInlineStart: value
  }),
  insetEnd: (value: string | number) => ({
    insetInlineEnd: value
  }),
  textAlign: (align: 'start' | 'end' | 'center' = 'start') => ({
    textAlign: align
  }),
  transform: (shouldFlip: boolean) => ({
    transform: shouldFlip && isRTL ? 'scaleX(-1)' : 'none'
  })
});